import React from 'react';
import { ArrowRight, Gem, ShieldCheck, Sparkles, MapPin, Fingerprint, BadgeCheck, Route, Award, Check } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, DIAMOND_SERVICES, DIAMOND_TECH, COMPANY, BETTERIDGE, MILESTONES, LRG_LUXURY, ZOE } from '../mock';

const ORBIT = [
  { label: 'Origin', icon: MapPin, angle: -90 },
  { label: 'Identity', icon: Fingerprint, angle: -18 },
  { label: 'Verified', icon: BadgeCheck, angle: 54 },
  { label: 'Journey', icon: Route, angle: 126 },
  { label: 'Pride Mark', icon: Award, angle: 198 },
];

const STATS = [
  { value: '100%', label: 'Botswana sourced, from rough to polished' },
  { value: '6', label: 'Verified stages, from source to certificate' },
  { value: 'Dedicated', label: 'Auction representation for selected clients' },
  { value: '24 / 7', label: 'Public verification through Botswana Brilliance' },
];

const PILLARS = [
  { icon: Gem, title: 'Ethically Sourced', text: 'Every stone begins with lawful Botswana documentation and responsible mining.' },
  { icon: ShieldCheck, title: 'Fully Traceable', text: 'Origin, custody and manufacturing recorded in one living certificate.' },
  { icon: Sparkles, title: 'Rigorously Graded', text: 'Cut, carat, clarity and colour assessed against our exacting standards.' },
];

const STAGES = ['Botswana Source', 'Rough Registration', 'Chain of Custody', 'Cutting and Polishing', 'Polished Identity', 'Botswana Brilliance Certificate'];
const SPECS = [['2.07', 'Carat'], ['D', 'Colour'], ['VVS1', 'Clarity'], ['Excellent', 'Cut']];

const SectionHead = ({ num, eyebrow, title, light, children }) => (
  <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
    <div className="lg:col-span-7">
      <p className={`eyebrow ${light ? 'eyebrow-light' : ''} mb-5`}>{num} / {eyebrow}</p>
      <h2 className={`font-serif text-4xl md:text-6xl leading-[1.02] ${light ? 'text-[#f7f4ee]' : 'text-[#0a1f38]'}`}>{title}</h2>
    </div>
    {children && <p className={`lg:col-span-5 font-light leading-relaxed text-lg ${light ? 'text-white/60' : 'text-[#3a3f47]'}`}>{children}</p>}
  </div>
);

const OrbitDiamond = () => (
  <div className="relative w-[320px] h-[320px] md:w-[440px] md:h-[440px] mx-auto" data-testid="diamond-orbit">
    <div className="absolute inset-0 rounded-full border border-[#c6a15b]/25" />
    <div className="absolute inset-[9%] rounded-full overflow-hidden shadow-[0_0_90px_rgba(198,161,91,0.18)]">
      <img src={IMAGES.diamondBlue} alt="Botswana diamond" className="w-full h-full object-cover scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#060d18]/70 via-transparent to-[#060d18]/30" />
    </div>
    <div className="absolute inset-0 orbit-ring">
      {ORBIT.map((o) => {
        const r = (o.angle * Math.PI) / 180;
        return (
          <div key={o.label} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${50 + 50 * Math.cos(r)}%`, top: `${50 + 50 * Math.sin(r)}%` }}>
            <span className="orbit-label flex items-center gap-1.5 whitespace-nowrap bg-[#0a1f38] border border-[#c6a15b]/30 text-[#f7f4ee] text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 rounded-full">
              <o.icon size={11} className="text-[#c6a15b]" /> {o.label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const Diamonds = () => {
  useScrollReveal();
  useSeo({ title: 'Botswana Diamonds and Botswana Brilliance', description: 'Rough and polished diamonds sourced exclusively from Botswana with dedicated auction representation, Botswana Brilliance origin verification and technology relationships with GemEx, iTraceiT and Sarine.', path: '/diamonds', image: '/images/diamonds-robinson.jpeg' });

  return (
    <div className="bg-[#060d18]">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32" data-testid="diamonds-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(18,49,79,0.6),transparent_60%)]" />
        <div className="relative container-lg grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7 reveal is-visible">
            <p className="eyebrow eyebrow-line eyebrow-light mb-8">Botswana Brilliance / Diamonds</p>
            <h1 className="font-serif text-[#f7f4ee] text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.98]">
              Every Botswana diamond<br />
              <span className="italic text-[#d9bd82]">carries its story.</span>
            </h1>
            <p className="text-white/65 font-light text-lg md:text-xl leading-relaxed max-w-xl mt-10">
              LRG Holdings sources rough and polished diamonds exclusively from Botswana, connecting each stone to its origin, its documented journey and the people behind its value.
            </p>
            <div className="flex flex-wrap gap-4 mt-12">
              <a href={DIAMOND_SERVICES[0].link} className="btn-gold" data-testid="diamonds-enquire-button">Enquire <ArrowRight size={15} /></a>
              <a href="https://www.botswana-brilliance.com" target="_blank" rel="noopener noreferrer" className="btn-outline btn-outline-light" data-testid="diamonds-verify-link">Verify a Diamond</a>
            </div>
          </div>
          <div className="lg:col-span-5 reveal is-visible"><OrbitDiamond /></div>
        </div>
      </section>

      {/* Stats ledger */}
      <section className="border-y border-white/10">
        <div className="container-lg grid grid-cols-2 lg:grid-cols-4 ledger-grid dark" data-testid="diamonds-stats">
          {STATS.map((s) => (
            <div key={s.value} className="p-8 md:p-10 reveal">
              <p className="font-serif text-5xl md:text-6xl text-[#f7f4ee]">{s.value}</p>
              <p className="text-white/50 text-sm font-light mt-3 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 01 Expertise */}
      <section className="py-28 bg-[#f7f4ee]">
        <div className="container-lg">
          <SectionHead num="01" eyebrow="Our Expertise" title="From rough to polished.">
            At LRG Holdings we bring years of experience across the diamond value chain. We take pride in sourcing our diamonds exclusively from Botswana, a country renowned for its ethical and responsible diamond mining practices.
          </SectionHead>
          <div className="grid md:grid-cols-3 ledger-grid">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="p-10 reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex items-start justify-between">
                  <span className="numeral text-3xl">0{i + 1}</span>
                  <p.icon className="text-[#c6a15b]" size={26} strokeWidth={1.3} />
                </div>
                <h3 className="font-serif text-3xl text-[#0a1f38] mt-10 mb-3">{p.title}</h3>
                <p className="text-[#6b6f76] font-light leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 Services */}
      <section className="py-28 bg-[#0a1f38]">
        <div className="container-lg">
          <SectionHead num="02" eyebrow="What We Do" title="Rough and polished." light>
            Two disciplines, one standard. Dedicated representation for rough buyers and a curated polished selection carrying Botswana provenance.
          </SectionHead>
          <div className="grid lg:grid-cols-2 gap-8">
            {DIAMOND_SERVICES.map((s, i) => (
              <article key={s.title} className="glass-card overflow-hidden reveal" style={{ transitionDelay: `${i * 120}ms` }} data-testid={`diamond-service-${i}`}>
                <div className="img-zoom h-72">
                  <img src={IMAGES[s.image]} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-10">
                  <p className="eyebrow eyebrow-light mb-3">0{i + 1} / Service</p>
                  <h3 className="font-serif text-4xl text-[#f7f4ee] mb-5">{s.title}</h3>
                  <p className="text-white/60 font-light leading-relaxed mb-8">{s.body}</p>
                  <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#d9bd82] tracking-[0.18em] uppercase text-xs font-semibold group">
                    {s.linkLabel} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage: Betteridge */}
      <section className="py-28 bg-[#efe9df]" data-testid="diamonds-heritage-section">
        <div className="container-lg">
          <SectionHead num="Heritage" eyebrow="Since 2014" title="Sitting at the table with the world.">
            LRG Holdings was founded in 2014, and its first chapter was written alongside one of America&rsquo;s most storied jewelers.
          </SectionHead>
          <div className="grid lg:grid-cols-12 ledger-grid">
            <div className="lg:col-span-5 p-10 reveal">
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#a3813f] mb-6">{BETTERIDGE.years}</p>
              <h3 className="font-serif text-5xl text-[#0a1f38]">{BETTERIDGE.name}</h3>
              <p className="font-serif italic text-2xl text-[#a3813f] mt-1">{BETTERIDGE.descriptor}</p>
              <dl className="mt-10 space-y-3">
                {BETTERIDGE.facts.map(([k, v]) => <div key={k} className="flex justify-between border-b border-[#0a1f38]/10 pb-2 text-sm"><dt className="text-[#6b6f76]">{k}</dt><dd className="text-[#0a1f38]">{v}</dd></div>)}
              </dl>
            </div>
            <div className="lg:col-span-7 p-10 reveal" style={{ transitionDelay: '120ms' }}>
              <p className="text-[#3a3f47] font-light text-lg leading-relaxed">From 2014 to 2016, LRG Holdings partnered with Betteridge, the Greenwich, Connecticut house founded in 1897 and described by The Wall Street Journal as Wall Street&rsquo;s Jeweler. Through the partnership Betteridge established an office in Botswana and, in January 2015, owner Terry Betteridge travelled to Gaborone to bid on rough diamonds at government auction, placing his company among the largest diamond buyers in the world alongside Tiffany and Lazare.</p>
              <blockquote className="font-serif text-2xl md:text-3xl text-[#0a1f38] leading-snug mt-8 pl-6 border-l-2 border-[#c6a15b]">&ldquo;{BETTERIDGE.quote}&rdquo;</blockquote>
              <p className="text-[#6b6f76] text-sm font-light mt-3">{BETTERIDGE.quoteBy}</p>
              <p className="text-[#3a3f47] font-light leading-relaxed mt-8">Those two years on the auction floor shaped the disciplined, dedicated auction representation that defines LRG&rsquo;s rough diamond business today.</p>
              <a href={BETTERIDGE.article} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-8 text-[#a3813f] tracking-[0.18em] uppercase text-xs font-semibold group" data-testid="betteridge-article-link">Read the Fairfield County Business Journal article <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></a>
            </div>
          </div>
          <div className="grid lg:grid-cols-12 ledger-grid mt-8" data-testid="lrg-luxury-section">
            <figure className="lg:col-span-7 p-3 reveal">
              <div className="img-zoom"><img src={IMAGES.versaceMelrose} alt="LRG Luxury Versace Jeans store at Melrose Arch, Johannesburg" className="w-full object-cover" /></div>
            </figure>
            <div className="lg:col-span-5 p-10 flex flex-col justify-center reveal" style={{ transitionDelay: '120ms' }}>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#a3813f] mb-6">{LRG_LUXURY.year} / Luxury Retail</p>
              <h3 className="font-serif text-5xl text-[#0a1f38]">{LRG_LUXURY.title}</h3>
              <p className="font-serif italic text-2xl text-[#a3813f] mt-1">{LRG_LUXURY.store}</p>
              <p className="text-[#6b6f76] text-xs tracking-[0.18em] uppercase mt-2">{LRG_LUXURY.city}</p>
              <p className="text-[#3a3f47] font-light leading-relaxed mt-8">{LRG_LUXURY.text}</p>
            </div>
          </div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-5 ledger-grid mt-8" data-testid="diamonds-timeline">
            {MILESTONES.map((m, i) => (
              <li key={i} className="p-7 reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <p className="numeral text-3xl">{m.year}</p>
                <h4 className="font-serif text-xl text-[#0a1f38] mt-4 mb-2">{m.title}</h4>
                <p className="text-[#6b6f76] font-light text-sm leading-relaxed">{m.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Nobel Laureate visit */}
      <section className="py-28 bg-[#f7f4ee]" data-testid="diamonds-robinson-section">
        <div className="container-lg grid lg:grid-cols-12 gap-14 items-center">
          <figure className="lg:col-span-7 reveal">
            <div className="img-zoom"><img src={IMAGES.diamondsRobinson} alt="Professor James A. Robinson, 2024 Nobel Laureate in Economic Sciences, examining a Botswana diamond with LRG Holdings and partners" className="w-full object-cover" /></div>
            <figcaption className="text-[#6b6f76] text-sm font-light mt-4 leading-relaxed">Professor James A. Robinson, 2024 Nobel Laureate in Economic Sciences, examining a Botswana diamond during his March 2026 visit with LRG Holdings.</figcaption>
          </figure>
          <div className="lg:col-span-5 reveal" style={{ transitionDelay: '120ms' }}>
            <p className="eyebrow eyebrow-line mb-6">Bringing the World to Botswana</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0a1f38] leading-[1.05]">Hosting Professor James A. Robinson, <span className="italic text-[#a3813f]">2024 Nobel Laureate in Economic Sciences.</span></h2>
            <p className="text-[#3a3f47] font-light leading-relaxed text-lg mt-8">In March 2026 LRG Holdings brought Professor Robinson, co-author of Why Nations Fail, to Botswana to see first hand how the country turned its diamond wealth into one of Africa&rsquo;s most enduring development stories. From the sorting floor to the boardroom, the visit connected the world&rsquo;s leading thinking on institutions with the people who steward Botswana&rsquo;s diamonds.</p>
          </div>
        </div>
      </section>

      {/* 03 Certificate */}
      <section className="py-28 bg-[#060d18]" data-testid="diamonds-certificate">
        <div className="container-lg">
          <SectionHead num="03" eyebrow="The Living Digital Passport" title="One identity, from the earth to the hand." light>
            Through Botswana Brilliance, a rough diamond is registered before manufacturing and its records travel with it. Each polished stone connects back to the rough it came from, held within one living certificate rather than a stack of separate papers.
          </SectionHead>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 glass-card overflow-hidden reveal">
              <div className="h-80 overflow-hidden">
                <img src={IMAGES.diamondBrilliant} alt="The Kalahari Light" className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <h3 className="font-serif text-3xl text-[#f7f4ee]">The Kalahari Light</h3>
                <p className="font-mono text-[#d9bd82] text-sm tracking-wider mt-1">BB-BW-2026-000117</p>
                <p className="text-white/40 text-[10px] tracking-[0.18em] uppercase mt-2">Illustrative certificate specimen</p>
                <div className="grid grid-cols-4 gap-4 mt-8 pt-8 border-t border-white/10">
                  {SPECS.map(([v, l]) => (
                    <div key={l}>
                      <p className="font-serif text-2xl text-[#f7f4ee]">{v}</p>
                      <p className="text-white/45 text-[10px] tracking-[0.18em] uppercase mt-1">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 glass-card p-8 md:p-12 reveal" style={{ transitionDelay: '120ms' }}>
              <p className="eyebrow eyebrow-light mb-8">Six verified stages</p>
              <ol className="divide-y divide-white/10">
                {STAGES.map((st, i) => (
                  <li key={st} className="flex items-center gap-6 py-5">
                    <span className="numeral text-2xl w-10 text-[#d9bd82]">0{i + 1}</span>
                    <span className="font-serif text-2xl text-[#f7f4ee] flex-1">{st}</span>
                    <span className="w-7 h-7 rounded-full border border-[#c6a15b]/50 flex items-center justify-center"><Check size={13} className="text-[#c6a15b]" /></span>
                  </li>
                ))}
              </ol>
              <a href="https://www.botswana-brilliance.com" target="_blank" rel="noopener noreferrer" className="btn-outline btn-outline-light mt-10">Explore the Certificate <ArrowRight size={15} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* 04 Technology */}
      <section className="py-28 bg-[#efe9df]">
        <div className="container-lg">
          <SectionHead num="04" eyebrow="Technology Partners" title="Innovation, in partnership.">
            LRG Holdings has established relationships with GemEx, iTraceiT and Sarine, bringing light-performance analysis, digital traceability and verification technology to the Botswana Brilliance framework.
          </SectionHead>
          <div className="grid md:grid-cols-3 ledger-grid">
            {DIAMOND_TECH.map((t, i) => (
              <div key={t.partner} className="p-10 reveal" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`diamond-tech-${i}`}>
                <div className="flex items-center justify-between mb-10">
                  <span className="numeral text-3xl">0{i + 1}</span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#a3813f] border border-[#c6a15b]/50 px-3 py-1">Relationship established {t.status}</span>
                </div>
                <div className="h-12 flex items-center mb-6"><img src={t.logo} alt={`${t.partner} logo`} className="max-h-10 max-w-[160px] w-auto object-contain" data-testid={`diamond-tech-logo-${i}`} /></div>
                <h3 className="font-serif text-3xl text-[#0a1f38] mb-4">{t.partner}</h3>
                <p className="text-[#6b6f76] font-light leading-relaxed mb-8">{t.body}</p>
                <a href={t.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#a3813f] tracking-[0.18em] uppercase text-xs font-semibold group">
                  Learn More <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem: Zoe Diamonds */}
      <section className="py-28 bg-[#0a1f38]" data-testid="zoe-diamonds-section">
        <div className="container-lg">
          <SectionHead num="05" eyebrow="Growing the Ecosystem" title="Trust that Botswana businesses can build on." light>
            {ZOE.intro}
          </SectionHead>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 glass-card p-10 md:p-12 reveal">
              <p className="eyebrow eyebrow-light mb-6">Welcoming {ZOE.name}</p>
              <p className="text-white/70 font-light leading-relaxed text-lg">{ZOE.body}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {['Botswana Brilliance ecosystem', 'iTraceiT traceability training', 'Natural diamond advocacy'].map((t) => <span key={t} className="text-[11px] tracking-[0.14em] uppercase border border-[#c6a15b]/40 px-3 py-1.5 text-[#d9bd82]">{t}</span>)}
              </div>
            </div>
            <div className="lg:col-span-5 glass-card p-10 md:p-12 flex flex-col justify-center reveal" style={{ transitionDelay: '120ms' }}>
              <blockquote className="font-serif text-2xl md:text-3xl text-[#f7f4ee] leading-snug">&ldquo;{ZOE.quote}&rdquo;</blockquote>
              <p className="font-serif text-xl text-[#d9bd82] mt-8">{ZOE.person}</p>
              <p className="text-white/50 text-xs tracking-[0.18em] uppercase mt-1">{ZOE.role}</p>
              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/10">
                <span className="text-white/40 text-[10px] tracking-[0.18em] uppercase">In partnership with</span>
                <img src="/images/logo-itraceit.svg" alt="iTraceiT" className="h-6 invert opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="relative py-32 overflow-hidden" data-testid="diamonds-cta">
        <img src={IMAGES.heroSavanna} alt="Botswana landscape" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d18] via-[#060d18]/70 to-[#060d18]" />
        <div className="relative container-lg text-center max-w-3xl mx-auto reveal">
          <p className="eyebrow eyebrow-light mb-6">From the Earth, Value</p>
          <h2 className="font-serif text-4xl md:text-6xl text-[#f7f4ee] leading-[1.02]">The diamond remains yours. <span className="italic text-[#d9bd82]">Its story becomes verifiable.</span></h2>
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            <a href={`mailto:${COMPANY.email}?subject=Diamond%20Enquiry`} className="btn-gold">Speak With LRG <ArrowRight size={15} /></a>
            <a href="https://www.botswana-brilliance.com" target="_blank" rel="noopener noreferrer" className="btn-outline btn-outline-light">Botswana Brilliance</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Diamonds;
